/* src/components/UI/LoadingSpinner.jsx */
import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

const LoadingSpinner = ({ 
  size = 'medium', 
  className = '', 
  text = '',
  variant = 'spinner'
}) => {
  const { theme } = useTheme();

  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-8 h-8',
    large: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const spinnerVariants = {
    spin: {
      rotate: 360,
      transition: {
        duration: 1,
        repeat: Infinity,
        ease: "linear"
      }
    }
  };

  const dotsVariants = {
    loading: {
      transition: {
        staggerChildren: 0.2,
        repeat: Infinity,
        repeatDelay: 0.5
      }
    }
  };

  const dotVariants = {
    loading: {
      y: [0, -10, 0],
      transition: {
        duration: 0.6,
        ease: "easeInOut"
      }
    }
  };

  const pulseVariants = {
    pulse: {
      scale: [1, 1.2, 1],
      opacity: [0.7, 1, 0.7],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'dots':
        return (
          <motion.div
            className="flex space-x-1"
            variants={dotsVariants}
            animate="loading"
          >
            {[0, 1, 2].map(i => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: theme.colors.primary }}
                variants={dotVariants}
              />
            ))}
          </motion.div>
        );

      case 'pulse':
        return (
          <motion.div
            className={`${sizeClasses[size]} rounded-full border-2`}
            style={{ 
              borderColor: theme.colors.primary,
              backgroundColor: `${theme.colors.primary}20`
            }}
            variants={pulseVariants}
            animate="pulse"
          />
        );

      default:
        return (
          <motion.div
            className={sizeClasses[size]}
            variants={spinnerVariants}
            animate="spin"
          >
            <div 
              className="w-full h-full border-2 border-transparent border-t-current rounded-full"
              style={{ color: theme.colors.primary }}
            />
          </motion.div>
        );
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {renderSpinner()}
      {text && (
        <motion.p
          className="mt-3 text-sm font-medium"
          style={{ color: theme.colors.textSecondary }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
};

export default LoadingSpinner;


